import {
  MAX_ORDER_NUMBERS,
  orderNumberKey,
  parseOrderNumbers,
  type Order,
} from "@estelle/domain";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface SelectByNumbersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The orders found. The caller ticks them, and says what it left out: the
   * numbers that matched nothing are `missing`, in the order they were given.
   */
  onFound: (orders: Order[], missing: string[]) => void;
}

/**
 * Paste order numbers, tick those orders (#621).
 *
 * Numbers as people write them — "14319", "EL14319", "#el14319" — split on
 * commas, spaces or new lines, and matched exactly by the server, so the
 * Orders list's own selection and "Print packing slips" do the rest. Nothing
 * is printed here: it only chooses which orders, across pages and filters.
 */
export function SelectByNumbers({
  open,
  onOpenChange,
  onFound,
}: SelectByNumbersProps) {
  const data = useDataSource();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const keys = parseOrderNumbers(text);
  const tooMany = keys.length > MAX_ORDER_NUMBERS;

  async function find() {
    setBusy(true);
    setMessage(null);
    try {
      const page = await data.orders.list({
        orderNumbers: keys,
        limit: MAX_ORDER_NUMBERS,
      });
      const found = new Set(
        page.items.map((order) => orderNumberKey(order.orderNumber)),
      );
      const missing = keys.filter((key) => !found.has(key));
      if (page.items.length === 0) {
        setMessage("None of those order numbers were found.");
        return;
      }
      onFound(page.items, missing);
      setText("");
      onOpenChange(false);
    } catch {
      setMessage("Could not look those up. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Select orders by number"
        description="Commas, spaces or lines between them."
      >
        <Textarea
          autoFocus
          rows={5}
          value={text}
          placeholder="14319, 14320, EL14321"
          aria-label="Order numbers"
          onChange={(event) => {
            setText(event.target.value);
            setMessage(null);
          }}
        />
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {message ??
            (tooMany
              ? `Up to ${String(MAX_ORDER_NUMBERS)} at a time; this has ${String(keys.length)}.`
              : keys.length > 0
                ? `${String(keys.length)} ${keys.length === 1 ? "number" : "numbers"} found in the text.`
                : "")}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={busy || keys.length === 0 || tooMany}
            onClick={() => {
              void find();
            }}
          >
            {busy ? "Finding…" : "Select orders"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
